import React, { useState, useRef, useEffect, useCallback } from 'react';
import './VideoLoader.css';

const VideoLoader = ({ onComplete }) => {
  const [fadingOut, setFadingOut] = useState(false);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  // Check if viewport is mobile or tablet (width <= 1024px)
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 1024;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use mobile-layout-video.mp4 for mobile and tablet, loader-screen video.mp4 for desktop
  const videoSrc = isMobileOrTablet
    ? process.env.PUBLIC_URL + '/mobile-layout-video.mp4'
    : process.env.PUBLIC_URL + '/loader-screen video.mp4';

  const finishLoader = useCallback(() => {
    setFadingOut(prev => {
      if (prev) return prev;
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 600);
      return true;
    });
  }, [onComplete]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Try unmuted play first; fallback to muted autoplay if browser blocks sound autoplay
    video.muted = false;
    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        console.warn('Unmuted autoplay blocked, falling back to muted autoplay:', err);
        video.muted = true;
        video.play().catch(() => {});
      });
    }

    // Stop video after 9 seconds as requested by user
    timerRef.current = setTimeout(() => {
      finishLoader();
    }, 9000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [finishLoader, videoSrc]);

  const handleTimeUpdate = () => {
    if (videoRef.current && videoRef.current.currentTime >= 9) {
      finishLoader();
    }
  };

  return (
    <div className={`video-loader-overlay ${fadingOut ? 'fade-out' : ''}`}>
      <video
        key={videoSrc}
        ref={videoRef}
        src={videoSrc}
        onTimeUpdate={handleTimeUpdate}
        onEnded={finishLoader}
        playsInline
        autoPlay
        className="loader-video"
      />
    </div>
  );
};

export default VideoLoader;
